import { useCallback, useEffect, useMemo, useState, ChangeEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AdvancedFilter, FilterConditionOutput, FilterStructure } from '@shared/components/ui/AdvancedFilter';
import { Search } from '@shared/components/ui/Search';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { ColumnDef } from '@tanstack/react-table';
import { WalletTransaction } from '../../models/Wallet';
import { cn } from '@shared/utils/utils';

interface FiltersSectionProps {
  columns: ColumnDef<WalletTransaction>[];
  selectedColumns: string[];
  onColumnsChange: (selected: string[]) => void;
  searchValue: string;
  onSearch: (value: string) => void;
  filterConditions: FilterConditionOutput[];
  onFilterChange: (conditions: FilterConditionOutput[]) => void;
  className?: string;
  /** Acciones extra al final de la barra (p. ej. exportar a Excel). */
  actions?: ReactNode;
}

export const FiltersSection = ({
  columns,
  selectedColumns,
  onColumnsChange,
  searchValue,
  onSearch,
  filterConditions,
  onFilterChange,
  className,
  actions,
}: FiltersSectionProps) => {
  const { t } = useTranslation('walletsModule');
  const { t: tUI } = useTranslation('ui');

  const [inputValue, setInputValue] = useState(searchValue);

  useEffect(() => {
    setInputValue((prev) => (prev === searchValue ? prev : searchValue));
  }, [searchValue]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleSearchSubmit = useCallback(() => {
    onSearch(inputValue.trim());
  }, [inputValue, onSearch]);

  const handleAdvancedFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      onFilterChange(conditions);
    },
    [onFilterChange],
  );

  const filterOptions: FilterStructure[] = useMemo(
    () => [
      {
        column: {
          label: t('filters.type.label'),
          value: 'type',
          type: 'select',
        },
        actions: [
          { label: t('filters.actions.in'), value: 'in' },
          { label: t('filters.actions.notIn'), value: 'not_in' },
          { label: t('filters.actions.isNull'), value: 'is_null' },
          { label: t('filters.actions.isNotNull'), value: 'is_not_null' },
        ],
        values: [
          { label: t('table.type.collection'), value: 'COLLECTION' },
          { label: t('table.type.refund'), value: 'REFUND' },
          { label: t('table.type.increase'), value: 'INCREASE' },
        ],
      },
      {
        column: {
          label: t('filters.description.label'),
          value: 'description',
          type: 'text',
        },
        actions: [
          { label: t('filters.actions.in'), value: 'in' },
          { label: t('filters.actions.notIn'), value: 'not_in' },
          { label: t('filters.actions.isNull'), value: 'is_null' },
          { label: t('filters.actions.isNotNull'), value: 'is_not_null' },
          { label: t('filters.actions.greaterThan'), value: 'greater_than' },
          { label: t('filters.actions.lessThan'), value: 'less_than' },
          { label: t('filters.actions.greaterEqual'), value: 'greater_equal' },
          { label: t('filters.actions.lessEqual'), value: 'less_equal' },
        ],
        values: null,
      },
      {
        column: {
          label: t('filters.balanceTotal.label'),
          value: 'balance_total',
          type: 'number',
        },
        actions: [
          { label: t('filters.actions.in'), value: 'in' },
          { label: t('filters.actions.notIn'), value: 'not_in' },
          { label: t('filters.actions.isNull'), value: 'is_null' },
          { label: t('filters.actions.isNotNull'), value: 'is_not_null' },
          { label: t('filters.actions.greaterThan'), value: 'greater_than' },
          { label: t('filters.actions.lessThan'), value: 'less_than' },
          { label: t('filters.actions.greaterEqual'), value: 'greater_equal' },
          { label: t('filters.actions.lessEqual'), value: 'less_equal' },
        ],
        values: null,
      },
      {
        column: {
          label: t('filters.amount.label'),
          value: 'amount',
          type: 'number',
        },
        actions: [
          { label: t('filters.actions.in'), value: 'in' },
          { label: t('filters.actions.notIn'), value: 'not_in' },
          { label: t('filters.actions.isNull'), value: 'is_null' },
          { label: t('filters.actions.isNotNull'), value: 'is_not_null' },
          { label: t('filters.actions.greaterThan'), value: 'greater_than' },
          { label: t('filters.actions.lessThan'), value: 'less_than' },
          { label: t('filters.actions.greaterEqual'), value: 'greater_equal' },
          { label: t('filters.actions.lessEqual'), value: 'less_equal' },
        ],
        values: null,
      },
    ],
    [t],
  );

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      {/* Buscador de billeteras - Wallet search */}
      <Search
        placeholder={t('search.placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="wallets-search"
        inputDataTestId="wallets-search-input"
      />

      {/* Filtros avanzados - Advanced filters */}
      <AdvancedFilter
        filterOptions={filterOptions}
        onChange={handleAdvancedFilterChange}
        initialConditions={filterConditions}
        translations={{
          filters: tUI('advancedFilter.filters', 'Filtros'),
          fulfillsWith: tUI('advancedFilter.fulfillsWith', 'Cumple con'),
          selectColumn: tUI('advancedFilter.selectColumn', 'Selecciona una columna'),
          selectAction: tUI('advancedFilter.selectAction', 'Selecciona una acción'),
          selectValue: tUI('advancedFilter.selectValue', 'Selecciona un valor'),
          addCondition: tUI('advancedFilter.addCondition', 'Agregar condición'),
          notFound: tUI('advancedFilter.notFound', 'Sin resultados'),
          and: tUI('advancedFilter.and', 'Y'),
          or: tUI('advancedFilter.or', 'O'),
          enterText: tUI('advancedFilter.enterText', 'Escribe un texto'),
          selectDate: tUI('advancedFilter.selectDate', 'Selecciona una fecha'),
          selectDateRange: tUI('advancedFilter.selectDateRange', 'Selecciona un rango'),
          from: tUI('advancedFilter.from', 'Desde'),
          to: tUI('advancedFilter.to', 'Hasta'),
        }}
      />

      {/* Selector de columnas - Column selector */}
      <ColumnSelector
        columns={columns}
        selected={selectedColumns}
        onColumnsChange={onColumnsChange}
        dataTestId="wallets-column-selector"
        storageKey="wallet-transactions-table"
      />

      {/* Acciones (p. ej. exportar a Excel) - Actions (e.g. export to Excel) */}
      {actions}
    </div>
  );
};
